import { useEffect, useMemo, useState } from "react";
import {
  fetchSectionItems,
  sectionEntryPath,
  type SectionItem,
} from "@/pages/admin/seo/sectionItems";
import { resolvePreview } from "@/pages/admin/seo/seoTemplate";
import SeoSnippetPreview from "@/pages/admin/seo/components/SeoSnippetPreview";
import ShareImageField from "@/pages/admin/seo/components/ShareImageField";
import { btnGhost, btnPrimary, textInput } from "@/pages/admin/components/AdminUI";
import type { SeoSetting } from "@/lib/adminContent";

interface ItemSeoOverridesProps {
  sectionRoot: string;
  rows: SeoSetting[];
  onSave: (
    path: string,
    label: string,
    patch: { title: string; description: string; keywords: string; og_image: string },
  ) => Promise<void>;
  onReset: (path: string) => Promise<void>;
  /** Drop the outer card chrome when the panel is rendered inside a modal. */
  embedded?: boolean;
}

const EMPTY_DRAFT = { title: "", description: "", keywords: "", og_image: "" };

/**
 * The per-item override layer. It lists every record under a section entry and
 * lets an editor give a single standout page its own search title/description.
 * A saved override is stored as an exact-path SEO entry, which always resolves
 * above the section-wide default.
 */
export default function ItemSeoOverrides({
  sectionRoot,
  rows,
  onSave,
  onReset,
  embedded = false,
}: ItemSeoOverridesProps) {
  const [items, setItems] = useState<SectionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState({ ...EMPTY_DRAFT });
  const [busy, setBusy] = useState(false);

  const sectionEntry = useMemo(
    () => rows.find((row) => row.path === sectionEntryPath(sectionRoot)) || null,
    [rows, sectionRoot],
  );

  const overridesByPath = useMemo(() => {
    const map = new Map<string, SeoSetting>();
    rows.forEach((row) => {
      if (!row.path.endsWith("/*")) map.set(row.path, row);
    });
    return map;
  }, [rows]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setItems([]);
    setEditing(null);
    fetchSectionItems(sectionRoot)
      .then((result) => {
        if (active) setItems(result);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [sectionRoot]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (item) => item.label.toLowerCase().includes(q) || item.path.toLowerCase().includes(q),
    );
  }, [items, query]);

  const startEdit = (item: SectionItem) => {
    const existing = overridesByPath.get(item.path);
    setEditing(item.path);
    setDraft({
      title: existing?.title || "",
      description: existing?.description || "",
      keywords: existing?.keywords || "",
      og_image: existing?.og_image || "",
    });
  };

  const submit = async (item: SectionItem) => {
    setBusy(true);
    try {
      await onSave(item.path, item.label, {
        title: draft.title.trim(),
        description: draft.description.trim(),
        keywords: draft.keywords.trim(),
        og_image: draft.og_image.trim(),
      });
      setEditing(null);
    } finally {
      setBusy(false);
    }
  };

  const clear = async (item: SectionItem) => {
    setBusy(true);
    try {
      await onReset(item.path);
      setEditing(null);
    } finally {
      setBusy(false);
    }
  };

  const body = (
    <div className={embedded ? "space-y-3" : "p-4 space-y-3"}>
      {items.length > 6 ? (
        <div className="relative">
          <span className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400">
            <i className="ri-search-line text-sm"></i>
          </span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Find an item…"
            className={`${textInput} pl-10`}
          />
        </div>
      ) : null}

      {loading ? (
        <p className="text-sm text-foreground-500 flex items-center gap-2">
          <i className="ri-loader-4-line animate-spin"></i> Loading items…
        </p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-foreground-500">
          {items.length === 0 ? "No items found in this section yet." : "No items match your search."}
        </p>
      ) : (
        <div className="space-y-2">
          {filtered.map((item) => {
            const existing = overridesByPath.get(item.path);
            const open = editing === item.path;
            const tokens = { title: item.label, description: item.summary };
            const previewTitle = resolvePreview(
              draft.title || sectionEntry?.title,
              tokens,
            );
            const previewDescription = resolvePreview(
              draft.description || sectionEntry?.description,
              tokens,
            );
            return (
              <div key={item.path} className="border border-background-200 rounded-md">
                <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground-900 truncate">{item.label}</p>
                    <p className="text-[11px] text-foreground-500 truncate">{item.path}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {existing ? (
                      <span className="text-[10px] font-semibold uppercase tracking-wider bg-accent-100 text-accent-900 border border-accent-300 px-2 py-0.5 rounded-full whitespace-nowrap">
                        Custom
                      </span>
                    ) : (
                      <span className="text-[10px] font-semibold uppercase tracking-wider bg-background-200 text-foreground-600 border border-background-300 px-2 py-0.5 rounded-full whitespace-nowrap">
                        Default
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => (open ? setEditing(null) : startEdit(item))}
                      className="w-8 h-8 flex items-center justify-center rounded-md border border-background-300 text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                      aria-label={open ? "Close editor" : "Edit override"}
                    >
                      <i className={open ? "ri-arrow-up-s-line" : "ri-edit-line"}></i>
                    </button>
                  </div>
                </div>

                {open ? (
                  <div className="px-3 pb-3 pt-3 border-t border-background-200 space-y-2.5">
                    <input
                      value={draft.title}
                      onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                      placeholder="Custom search title (leave blank to use the default)"
                      className={textInput}
                    />
                    <textarea
                      value={draft.description}
                      onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                      rows={2}
                      maxLength={320}
                      placeholder="Custom meta description"
                      className={`${textInput} resize-none`}
                    ></textarea>
                    <input
                      value={draft.keywords}
                      onChange={(event) => setDraft({ ...draft, keywords: event.target.value })}
                      placeholder="keywords, comma, separated"
                      className={textInput}
                    />

                    <div className="space-y-2">
                      <ShareImageField
                        value={draft.og_image}
                        onChange={(next) => setDraft({ ...draft, og_image: next })}
                      />

                      <SeoSnippetPreview
                        title={previewTitle}
                        description={previewDescription}
                        path={item.path}
                        image={draft.og_image.trim() || existing?.og_image || ""}
                        tokens={tokens}
                      />
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => submit(item)}
                        disabled={busy}
                        className={btnPrimary}
                      >
                        {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                        Save override
                      </button>
                      {existing ? (
                        <button
                          type="button"
                          onClick={() => clear(item)}
                          disabled={busy}
                          className={btnGhost}
                        >
                          <i className="ri-eraser-line"></i> Reset to default
                        </button>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  if (embedded) return body;

  return (
    <div className="border border-background-200 rounded-lg bg-background-50">
      <div className="px-4 py-3.5 border-b border-background-200">
        <p className="text-sm font-semibold text-foreground-950">Per-item overrides</p>
        <p className="text-xs text-foreground-600 mt-1">
          Give one standout page its own search title and description. It always wins over the
          section default above.
        </p>
      </div>
      {body}
    </div>
  );
}